<template>
  <main class="withdraw-target">
    <header class="withdraw-target__header">
      <button type="button" :aria-label="$t('frontend.common.back')" @click="back"><van-icon name="arrow-left" /></button>
      <h1>{{ $t('frontend.finance.withdraw') }}</h1>
      <span></span>
    </header>

    <section class="withdraw-target__form">
      <label class="withdraw-target__group">
        <span>{{ $t('frontend.finance.withdrawal_method') }}</span>
        <button type="button" class="withdraw-target__radio" @click="showMethodSheet = true"><van-icon name="checked" />{{ $t('frontend.finance.virtual_address') }}</button>
      </label>
      <label class="withdraw-target__group">
        <span>{{ $t('frontend.finance.wallet_network') }}</span>
        <button v-if="selectedAccount" type="button" class="withdraw-target__radio" @click="showMethodSheet = true"><van-icon name="checked" />{{ withdrawMethodLabel }}({{ selectedAccount.value }})</button>
        <button v-else type="button" class="withdraw-target__link" @click="$router.push(bindingRoute)">{{ selectedAccountPrompt }}</button>
      </label>
      <label class="withdraw-target__group withdraw-target__group--amount">
        <span>{{ $t('frontend.finance.withdraw_short') }}</span>
        <div>
          <b>$</b><input v-model="withdrawMoney" type="number" inputmode="decimal" placeholder="0.00"><button type="button" @click="allMoney">{{ $t('frontend.finance.all_lower') }}</button>
        </div>
        <small>{{ $t('frontend.finance.remaining_amount', { amount: balance }) }}</small>
      </label>
    </section>

    <button class="withdraw-target__submit" type="button" :disabled="submitting" @click="doWithdraw">{{ submitting ? $t('common.loading') : $t('frontend.finance.withdraw_now') }}</button>
    <van-action-sheet v-model="showMethodSheet" class="withdraw-method-sheet" :actions="methodActions" :cancel-text="$t('common.cancel')" @select="selectMethod" />
  </main>
</template>

<script>
import { displayNumber } from "../../common/reference";
import { bindingData, bindingError } from "../../common/binding";

export default {
  data: () => ({
    showPopover: false,
    showMethodSheet: false,
    submitting: false,
    withdrawMoney: "",
    withdrawMethod: "TRC20",
    userInfo: {},
    bankInfo: null,
    usdtInfo: { trc20: "", erc20: "", btc: "" },
    withdrawRole: {},
    methods: ["TRC20", "ERC20", "BTC"]
  }),
  computed: {
    balance() { return displayNumber(this.userInfo.money); },
    withdrawMethodLabel() { return this.withdrawMethod; },
    methodActions() { return this.methods.map(value => ({ value, name: value })); },
    maskedBankAccount() {
      const account = String(this.bankInfo && this.bankInfo.bankid || "");
      return account.length > 4 ? `**** ${account.slice(-4)}` : account;
    },
    selectedAccount() {
      const address = this.usdtInfo[this.withdrawMethod.toLowerCase()];
      return address ? { label: this.$t('frontend.finance.address', { method: this.withdrawMethod }), value: address } : null;
    },
    bindingRoute() { return "/BindUsdt"; },
    selectedAccountPrompt() { return this.$t('frontend.finance.bind_address_first', { method: this.withdrawMethod }); }
  },
  methods: {
    ruleValue(key, fallback) {
      const value = this.withdrawRole[key];
      const number = Number(value);
      return value !== undefined && value !== null && value !== '' && Number.isFinite(number) && number >= 0 ? number : fallback;
    },
    ruleAmount(key, fallback) { return this.ruleValue(key, fallback).toFixed(2); },
    back() { window.history.back(); },
    async load() {
      try {
        const responses = await Promise.all([
          this.$http({ method: "get", url: "user_info" }),
          this.$http({ method: "get", url: "user_get_withdraw_role" }),
          this.$http({ method: "get", url: "user_get_usdt" })
        ]);
        this.userInfo = bindingData(responses[0]);
        this.withdrawRole = bindingData(responses[1]);
        const usdt = bindingData(responses[2]);
        this.usdtInfo = { trc20: usdt.trc20 || "", erc20: usdt.erc20 || "", btc: usdt.btc || "" };
      } catch (error) {
        bindingError(this, error, this.$t('frontend.finance.withdraw_load_failed'));
      }
    },
    selectMethod(action) {
      this.withdrawMethod = action.value || action.name;
      this.showMethodSheet = false;
    },
    allMoney() { this.withdrawMoney = this.userInfo.money || ""; },
    async doWithdraw() {
      const money = Number(this.withdrawMoney);
      if (!Number.isFinite(money) || money <= 0) {
        this.$toast(this.$t('frontend.finance.withdraw_invalid_amount'));
        return;
      }
      if (!this.selectedAccount) {
        this.$toast(this.selectedAccountPrompt);
        this.$router.push(this.bindingRoute);
        return;
      }
      this.submitting = true;
      try {
        const response = await this.$http({
          method: "post",
          url: "user_set_withdraw",
          data: {
            money: this.withdrawMoney,
            withdraw_method: this.withdrawMethod
          }
        });
        if (Number(response.code) !== 200) {
          const error = new Error(response.msg || this.$t('frontend.finance.withdraw_failed'));
          error.code = Number(response.code);
          throw error;
        }
        this.$toast(response.msg || this.$t('frontend.finance.withdraw_submitted'));
        this.withdrawMoney = "";
        await this.load();
      } catch (error) {
        bindingError(this, error, this.$t('frontend.finance.withdraw_failed'));
      } finally {
        this.submitting = false;
      }
    }
  },
  created() {
    if (!localStorage.getItem("token")) this.$router.push("/Login");
    else this.load();
  }
};
</script>

<style scoped>
.withdraw-target { min-height: 100vh; padding-bottom: 1rem; background: #f7f7fa; color: #2d2636; }
.withdraw-target__header { height: 3.3125rem; display: grid; grid-template-columns: 2.6875rem 1fr 2.6875rem; align-items: center; background: #fff; }
.withdraw-target__header button { border: 0; background: transparent; color: #443a50; font-size: 1.2rem; }
.withdraw-target__header h1 { margin: 0; font-size: 1.25rem; font-weight: 700; text-align: center; }
.withdraw-target__form { padding: 0.56rem 0.6875rem 0; background: #fff; }
.withdraw-target__group { display: block; margin-bottom: 0.75rem; color: #5c5364; font-size: 0.68rem; line-height: 1; }
.withdraw-target__group > span { display: block; margin-bottom: 0.4375rem; }
.withdraw-target__radio, .withdraw-target__link { display: flex; align-items: center; gap: 0.24rem; padding: 0; border: 0; color: #2e2638; background: transparent; font-size: 0.8rem; text-align: left; }
.withdraw-target__radio .van-icon { color: #7d3fb3; font-size: 1.1rem; }
.withdraw-target__link { min-height: 1.125rem; color: #7d3fb3; }
.withdraw-target__group--amount { margin-bottom: 0; }
.withdraw-target__group--amount > div { display: flex; align-items: center; border-bottom: 0.04rem solid #e9e6ed; }
.withdraw-target__group--amount b { margin-right: 0.2rem; color: #342b3d; font-size: 0.82rem; }
.withdraw-target__group--amount input { flex: 1; min-width: 0; border: 0; outline: 0; color: #302739; background: transparent; font-size: 0.88rem; }
.withdraw-target__group--amount button { border: 0; color: #857a8d; background: transparent; font-size: 0.75rem; }
.withdraw-target__group small { display: block; margin-top: 0.35rem; color: #756d7b; font-size: 0.72rem; }
.withdraw-target__submit { display: block; width: calc(100% - 1.375rem); height: 1.625rem; margin: 0.875rem auto 0; border: 0; border-radius: 0.24rem; color: #fff; background: #8754bb; font-size: 0.85rem; font-weight: 700; }
.withdraw-target__submit:disabled { opacity: .6; }
</style>
